<script setup lang="ts">
import type { NovaModalCommonEmits } from './NovaModalCommon.types'

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<NovaModalCommonEmits>()

const slots = useSlots()
</script>

<template>
  <vue-final-modal
    v-slot="{ params }"
    v-bind="$attrs"
    classes="modal-container"
    :content-class="['modal-content', $attrs.name, { full: $attrs.mobileFull }]"
  >
    <NovaBoxBase
      v-if="$attrs.modelValue"
      :class="[
        'modal-box',
        `width-${$attrs.width}`,
        { 'has-inner-scroll': $attrs.hasInnerScroll },
      ]"
      :modal="{ type: 'modal', isMobileFull: $attrs.mobileFull || false }"
      :round="20"
    >
      <div v-if="slots.title || $attrs.showCloseBtn" class="modal-header">
        <h4 v-if="slots.title" class="title">
          <slot name="title" />
        </h4>
        <div
          v-if="$attrs.showCloseBtn"
          class="close-wrap"
          @click="
            () =>
              $attrs.type === 'create' ? emit('customClose') : emit('close')
          "
        >
          <NovaButtonIcon
            :icon="{ type: 'outline', name: 'close-extend' }"
            class="btn-close"
          />
        </div>
      </div>

      <NovaLayoutScrollContainer
        v-if="$attrs.modelValue && slots.contents"
        :lock-scroll-container="$attrs.hasInnerScroll || false"
        class="modal-contents"
      >
        <slot name="contents" />
      </NovaLayoutScrollContainer>

      <div
        v-if="$attrs.modelValue && slots.noScrollContents"
        class="no-scroll-area"
        :style="{
          'max-height': $attrs.maxHeight ? `${$attrs.maxHeight}px` : '',
        }"
      >
        <slot name="noScrollContents" :params="params" />
      </div>

      <div v-if="$attrs.actions?.isShow" class="modal-actions">
        <NovaButtonText
          :label="$attrs.actions.label[0]"
          :theme="'secondary-gray-200'"
          :size="32"
          class="btn-action"
          @click="emit('cancel')"
        />
        <NovaButtonText
          :label="$attrs.actions.label[1]"
          :theme="'primary-blue-dark'"
          :size="32"
          :disabled="$attrs.actions.disabled"
          class="btn-action"
          @click="emit('confirm')"
        />
      </div>
    </NovaBoxBase>
  </vue-final-modal>
</template>

<style lang="scss" scoped>
:deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

:deep(.vfm--overlay) {
  background-color: hex-to-rgba($color-black, 0.65);
}

:deep(.modal-content) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  width: 100%;
  height: 100%;
}

.modal-box {
  padding: 28px 20px 37px;
  width: 100%;
  max-width: 670px;
  max-height: 100%;

  &.has-inner-scroll {
    padding: 0;
    overflow: hidden;

    .modal-header {
      flex-shrink: 0;
      padding: 28px 20px 30px;
      margin-bottom: 0;
    }
  }

  &.width {
    &-400 {
      max-width: 400px;
    }
  }

  .modal-header {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 30px;
    width: 100%;

    > .title {
      flex-grow: 1;
      @include text-style($text-heading2-bold);
    }

    .close-wrap {
      font-size: 0;

      .btn-close {
        color: $color-text-2;
      }
    }
  }

  .modal-contents {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .modal-actions {
    display: flex;
    margin-top: 20px;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    > .btn-action {
      width: 100px;
    }
  }

  .no-scroll-area {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: 100%;
  }
}

@include mobile {
  :deep(.modal-content) {
    padding: 0 20px;

    .modal-box {
      .modal-header {
        flex-shrink: 0;
        padding: 16px 20px;
        margin-bottom: 0;

        > .title {
          @include text-style($text-heading3-bold);
        }
      }
    }

    &.full {
      padding: 0;
      height: 100%;
    }
  }

  .modal-box {
    .modal-actions {
      display: flex;
      margin-top: 0;
      padding: 0 20px 20px;
      gap: 8px;

      > .btn-action {
        width: 50%;
      }
    }

    &:not(.has-inner-scroll) {
      .modal-contents {
        padding: 0 20px 20px;
      }
    }

    .no-scroll-area {
      flex-grow: 1;
      /* overflow-y: overlay; */
    }
  }
}
</style>
